// SPEC-426 M2 C.1 — single-op canvas applier, shared by the web store (applyOps loop) and the
// worker mirror (agent.ts re-exports it as applyOpLocally). Zero-dependency pure TS: both sides
// consume this module at build/bundle time only — same precedent as layout.ts (SPEC-425 §A.7).
// No runtime cross worker/web import (CF Worker and Pages build separately; esbuild/tsc inline it).
//
// Convergence note (SPEC-426 M2): before extraction the two copies (worker agent.ts
// applyOpLocally vs web store.ts applyOps) had drifted in four spots. Merged semantics = union
// of both, so neither side loses behavior it depends on; each resolution is documented in the
// PR description. Additions on top of both old copies:
//   - `move` clamps the target box into the canvas bounds (out-of-bounds = pull back to the
//     edge). Defense in depth: worker tools.move_object already clamps at validation time —
//     this also covers ops produced by older workers and keeps mirror ≡ web by construction.
//   - add ops pass through optional `name`/`role` (B.1 named layer tree).
// The web-only runtime field `_born` (SPEC-409 ghost cursor) is deliberately NOT handled here —
// store.applyOps stamps it after each add op, keeping this module pure and mirror-safe.

import { collectDescendants, hasLayout, resolveLayout, type LayoutOptions, type LayoutSpec } from './layout';
// SPEC-462 M1: update_text sizing/w 通道重导高度的唯一来源（textMetrics — 渲染器/布局引擎/
// 审计共享的度量 SoT；layout.ts 同级的纯兄弟模块，双端构建期内联先例不变）。
import { deriveTextSize, type TextSizing } from './textMetrics';

/** Minimal structural object shape the applier needs (CanvasObject-compatible on both sides). */
export interface OpObjectLike {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  // image
  src?: string | null;
  job_id?: string;
  pending?: boolean;
  error?: string | null;
  // SPEC-463 M1.1: 槽真宽高 pin（worker tools 生成时 stamp；JOB_UPDATE 卡尺优先 slot_h）
  slot_w?: number;
  slot_h?: number;
  // SPEC-464 M2: fit 拟合语义（'cover'|'contain'|'fill'，发射侧 IMAGE_FIT 门控）+ JOB_UPDATE
  // pin 的源图 natural size（源比例 SoT — worker facts/审计面只读）。absent = 旧对象/off 面。
  fit?: string;
  src_w?: number;
  src_h?: number;
  // text
  text?: string;
  font_size?: number;
  color?: string;
  font_weight?: string;
  align?: string;
  // rect / frame
  fill?: string;
  stroke?: string;
  stroke_width?: number;
  radius?: number;
  layout?: LayoutSpec;
  parent?: string;
  // SPEC-455 M1: engine-derived sizing (text: auto/fixed-width; frame: fit_content/fill_container);
  // absent = legacy bare w/h (byte-identical passthrough).
  sizing?: string;
  // SPEC-462 M2: layer positioning — 'absolute' = out-of-flow child anchored to the parent's
  // border-box; absent = in-flow legacy behavior (byte-identical passthrough).
  position?: string;
  // SPEC-426 M2 B.1: semantic layer name + optional role annotation
  name?: string;
  role?: string;
  // SPEC-457 M1: icon object — iconData 白名单图标名；`color`（上方）兼任描边色
  icon?: string;
}

export interface OpCanvasLike<T extends OpObjectLike> {
  artboard: { w: number; h: number; bg: string };
  objects: T[];
}

/** Structural op union — both sides' CanvasOp unions are assignable to this. */
export type OpLike =
  | { op: 'add_image'; id: string; x: number; y: number; w: number; h: number; src?: string | null; job_id?: string; parent?: string; name?: string; role?: string; layer?: 'front' | 'back'; slot_w?: number; slot_h?: number; position?: 'absolute'; fit?: string }
  // SPEC-457 M1: 单色矢量图标（color=描边色，缺省继承文字默认色）
  | { op: 'add_icon'; id: string; x: number; y: number; w: number; h: number; icon: string; color?: string; parent?: string; name?: string; role?: string; position?: 'absolute' }
  | { op: 'set_image'; id: string; src?: string | null; job_id?: string; pending?: boolean }
  | { op: 'add_text'; id: string; text: string; x: number; y: number; w?: number; font_size?: number; color?: string; font_weight?: string; align?: string; parent?: string; name?: string; role?: string; sizing?: string; position?: 'absolute' }
  | { op: 'add_rect'; id: string; x: number; y: number; w: number; h: number; fill?: string; stroke?: string; stroke_width?: number; radius?: number; parent?: string; name?: string; role?: string; position?: 'absolute' }
  | { op: 'add_frame'; id: string; x: number; y: number; w: number; h: number; layout?: LayoutSpec; fill?: string; stroke?: string; stroke_width?: number; radius?: number; parent?: string; name?: string; role?: string; sizing?: string; position?: 'absolute' }
  // SPEC-462 M1: sizing/w 修复通道（旧 op 无两字段 → apply 守卫短路，byte-identical）
  | { op: 'update_text'; id: string; text?: string; color?: string; font_size?: number; font_weight?: string; align?: string; sizing?: string; w?: number }
  // SPEC-464 M2: fit 拟合语义修复通道（发射侧 IMAGE_FIT 门控；旧路径零此 op）
  | { op: 'update_image'; id: string; fit: string }
  | { op: 'move'; id: string; x: number; y: number }
  | { op: 'remove'; id: string }
  | { op: 'reorder'; id: string; mode: 'back' | 'front' }
  // SPEC-461 M1（RC1 根治）: 流安全直接更新面 — frame 布局参数 patch（子元素自动重排，
  // resolveLayout 公式不动）+ 显式 reparent（frame id=挂入流尾；null=脱流保留绝对坐标的受控出口）。
  | { op: 'update_layout'; id: string; direction?: 'row' | 'column'; gap?: number; padding?: number | [number, number, number, number]; align?: 'start' | 'center' | 'end'; justify?: 'start' | 'center' | 'end' | 'space-between' }
  | { op: 'set_parent'; id: string; parent: string | null }
  // SPEC-462 M2: 脱流语义通道 — 设/清 position:'absolute'（变更后由尾部 resolveLayout 重算）。
  | { op: 'set_position'; id: string; position: 'absolute' | null }
  | { op: 'set_bg'; color: string };

/** Optional name/role spread — conditional so old calls (no name) stay byte-identical. */
function nameRole(o: { name?: string; role?: string }): { name?: string; role?: string } {
  return {
    ...(o.name !== undefined ? { name: o.name } : {}),
    ...(o.role !== undefined ? { role: o.role } : {}),
  };
}

/** Optional sizing spread (SPEC-455 M1) — conditional so ops without sizing stay byte-identical. */
function sizingOf(o: { sizing?: string }): { sizing?: string } {
  return o.sizing !== undefined ? { sizing: o.sizing } : {};
}

/** Optional position spread (SPEC-462 M2) — conditional so ops without position stay byte-identical. */
function positionOf(o: { position?: string }): { position?: string } {
  return o.position !== undefined ? { position: o.position } : {};
}

/** SPEC-456 — stack-aware placement: layer:'back' → bottom of its stack (parented: immediately
 * after the parent object = parent 子栈栈底、parent 自身之上; root: index 0 = 全画布最底).
 * 缺省/'front' → append to the end (legacy push, byte-identical). parent 存在但未找到（防御，
 * tools 层 checkParent 已拦）→ 按根级垫底。 */
function insertByLayer<T extends OpObjectLike>(objects: T[], obj: T, layer?: string): void {
  if (layer !== 'back') { objects.push(obj); return; }
  const pi = obj.parent !== undefined ? objects.findIndex(o => o.id === obj.parent) : -1;
  objects.splice(pi >= 0 ? pi + 1 : 0, 0, obj);
}

/**
 * Apply one canvas op; returns a new canvas (objects array copied; matched targets are mutated
 * in place — same allocation/mutation profile as both pre-convergence copies).
 * Parented children sit at engine-derived coordinates (resolveLayout pass at the end);
 * legacy canvases (no frames/parent links) skip the pass entirely.
 * `options` forwards to resolveLayout (SPEC-455 M1 kill-switch: { physics: false } = legacy
 * positions-only pass, sizes untouched).
 */
export function applyOpLocally<T extends OpObjectLike>(canvas: OpCanvasLike<T>, op: OpLike, options?: LayoutOptions): OpCanvasLike<T> {
  let objects: T[] = [...canvas.objects];
  switch (op.op) {
    case 'add_image': {
      // Merged (web semantics): `pending: !src` marks generation placeholders — the worker mirror
      // previously omitted it, making its brief under-report in-flight images.
      // SPEC-463 M1.1: 槽真宽高 pin 条件透传（旧 op 无字段 → 零新键，byte-identical）。
      // SPEC-462 M2: position 条件透传（positionOf，旧 op 无字段 → byte-identical）；两套语义并存。
      const img = { id: op.id, type: 'image', x: op.x, y: op.y, w: op.w, h: op.h, src: op.src, job_id: op.job_id, pending: !op.src, parent: op.parent, ...nameRole(op), ...(op.slot_w !== undefined ? { slot_w: op.slot_w } : {}), ...(op.slot_h !== undefined ? { slot_h: op.slot_h } : {}), ...positionOf(op), ...(op.fit !== undefined ? { fit: op.fit } : {}) } as T;
      // SPEC-456: layer:'back' 垫底插入（insertByLayer）；缺省/'front' = push 数组尾，旧行为逐字节不变。
      insertByLayer(objects, img, op.layer);
      break;
    }
    case 'add_icon':
      // SPEC-457 M1: 单色矢量图标 — 同步落画布（无 pending/job）；color 直传（缺省 = 渲染器
      // 继承文字默认色）；无 layer 参数（M1 范围），push 数组尾与 add_text/add_rect 同构。
      objects.push({ id: op.id, type: 'icon', x: op.x, y: op.y, w: op.w, h: op.h, icon: op.icon, color: op.color, parent: op.parent, ...nameRole(op), ...positionOf(op) } as T);
      break;
    case 'add_text':
      objects.push({ id: op.id, type: 'text', x: op.x, y: op.y, w: op.w ?? 600, h: (op.font_size ?? 48) * 1.4, text: op.text, font_size: op.font_size ?? 48, color: op.color ?? '#1a1a1a', font_weight: op.font_weight ?? 'normal', align: op.align ?? 'left', parent: op.parent, ...nameRole(op), ...sizingOf(op), ...positionOf(op) } as T);
      break;
    case 'add_rect':
      objects.push({ id: op.id, type: 'rect', x: op.x, y: op.y, w: op.w, h: op.h, fill: op.fill, stroke: op.stroke, stroke_width: op.stroke_width, radius: op.radius, parent: op.parent, ...nameRole(op), ...positionOf(op) } as T);
      break;
    case 'add_frame':
      objects.push({ id: op.id, type: 'frame', x: op.x, y: op.y, w: op.w, h: op.h, layout: op.layout, fill: op.fill, stroke: op.stroke, stroke_width: op.stroke_width, radius: op.radius, parent: op.parent, ...nameRole(op), ...sizingOf(op), ...positionOf(op) } as T);
      break;
    case 'update_text': {
      const t = objects.find(o => o.id === op.id);
      // Merged (web semantics): a font_size change resyncs the estimated text-box height —
      // the worker mirror previously skipped this and drifted from the real canvas.
      // SPEC-460 M2-2: font_weight/align patch 直落（add_text 同字段名；无几何重算 —
      // 字重/对齐不改估算文本盒高，font_size 的 h resync 语义保持不变）。
      if (t) {
        if (op.text) t.text = op.text;
        if (op.color) t.color = op.color;
        if (op.font_size) { t.font_size = op.font_size; t.h = op.font_size * 1.4; }
        if (op.font_weight) t.font_weight = op.font_weight;
        if (op.align) t.align = op.align;
        // SPEC-462 M1: sizing/w 修复通道直落 + deriveTextSize（textMetrics）单一来源重导 w/h —
        // 双端一致红线（web store 与 worker mirror 共享本 applier，spec426 mirror-parity 纪律）。
        // 旧 op 无两字段 → 守卫短路，旧路径 byte-identical（自门控，无 env 依赖；发射侧由
        // TEXT_SIZING_DEFAULT 门控）。只给 w 无 sizing = 'fixed-width'（add_text 默认规则同源）；
        // 非枚举垃圾值防御性归 'fixed-width'（发射侧已校验，此处仅 backstop）。
        if (op.sizing !== undefined || op.w !== undefined) {
          if (op.w !== undefined) t.w = op.w;
          // 非枚举垃圾值防御性归 'fixed-width'（发射侧已校验，此处仅 backstop）；局部 const
          // 承载收窄（泛型 T 属性赋值 narrowing 不可靠，tsc 实证）。
          const candidate = op.sizing !== undefined ? op.sizing : t.sizing;
          const tsizing: TextSizing = candidate === 'auto' ? 'auto' : 'fixed-width';
          t.sizing = tsizing;
          const d = deriveTextSize(t.text ?? '', t.font_size ?? 48, t.font_weight, tsizing, t.w);
          t.w = d.w;
          t.h = d.h;
        }
      }
      break;
    }
    case 'update_image': {
      // SPEC-464 M2: fit 拟合语义 patch（update_text 同构）。不存在 id = no-op（update_text/move
      // 守卫一致）；目标非 image 由 tools 层挡，此处直落字段（set_position 同构防御）。
      // 旧路径零此 op（发射侧 IMAGE_FIT 门控），无 byte-identical 风险。
      const t = objects.find(o => o.id === op.id);
      if (t) t.fit = op.fit;
      break;
    }
    case 'move': {
      const t = objects.find(o => o.id === op.id);
      // SPEC-461 M1.1（RC1 根治）: 流安全 move — 有 parent 的目标不再静默脱流（旧 SPEC-425
      // detach 语义 = 「每次位置修复即破坏结构」的直接来源：流内对象被摘出冻结在绝对坐标，
      // 兄弟回流后互相压盖）。流内对象坐标写入整体跳过 — 位置由布局引擎所有，写了也会被
      // 尾部 resolveLayout 覆盖；tools.ts move_object 前置检查已把这类调用挡成引导性 error，
      // 这里是 legacy ops / 前端 applyOps 直发路径的 backstop。前端手动拖拽走 store.ts
      // PATCH_OBJECT reducer（脱流语义保留，SPEC-461 明示不改人手交互）。
      // SPEC-426 M2 C.1: 无 parent 对象维持 clamp 入画布行为（out-of-bounds = 拉回边缘）。
      if (t && t.parent === undefined) {
        const maxX = Math.max(0, canvas.artboard.w - t.w);
        const maxY = Math.max(0, canvas.artboard.h - t.h);
        t.x = Math.min(Math.max(op.x, 0), maxX);
        t.y = Math.min(Math.max(op.y, 0), maxY);
      }
      break;
    }
    case 'update_layout': {
      // SPEC-461 M1.2: frame 布局参数 patch（给字段才写；resolveLayout 公式不动）— 非 frame /
      // 不存在 id = no-op（防御性；tools 层已挡）。改后子元素由尾部 resolveLayout 重排。
      const t = objects.find(o => o.id === op.id);
      if (t && t.type === 'frame') {
        const layout: LayoutSpec = { direction: 'row', ...(t.layout ?? {}) };
        if (op.direction !== undefined) layout.direction = op.direction;
        if (op.gap !== undefined) layout.gap = op.gap;
        if (op.padding !== undefined) layout.padding = op.padding;
        if (op.align !== undefined) layout.align = op.align;
        if (op.justify !== undefined) layout.justify = op.justify;
        t.layout = layout;
      }
      break;
    }
    case 'set_parent': {
      // SPEC-461 M1.3: 显式 reparent。parent=frame id → 挂入该 frame 流尾（数组序插到该 frame
      // 最后一个兄弟之后；无兄弟紧随 frame — reorder 'front' 同款锚定）；null → 脱流并保留
      // 当前绝对坐标（「确实要绝对定位」的受控出口），数组原位不动（z 序不变）。
      // 防御：目标 = 自身/子孙（成环）或 frame 不存在 = no-op（tools 层已挡，防 legacy ops）。
      const i = objects.findIndex(o => o.id === op.id);
      if (i >= 0) {
        if (op.parent === null) {
          objects[i].parent = undefined;
        } else if (op.parent !== op.id && !collectDescendants(objects, op.id).includes(op.parent)) {
          const pi = objects.findIndex(o => o.id === op.parent);
          if (pi >= 0) {
            const [moved] = objects.splice(i, 1);
            moved.parent = op.parent;
            let anchor = objects.findIndex(o => o.id === op.parent);
            for (let k = objects.length - 1; k > anchor; k--) {
              if (objects[k].parent === op.parent) { anchor = k; break; }
            }
            objects.splice(anchor + 1, 0, moved);
          }
        }
      }
      break;
    }
    case 'set_position': {
      // SPEC-462 M2: 设/清 position:'absolute'。设 = 脱流（尾部 resolveLayout 把它从流 pass
      // 滤除并锚定父 border-box，不再占流位/不影响兄弟）；null = 清除归流（回 content-box
      // 流内排布）。不存在 id / 无 parent 对象 = 字段照写，resolveLayout 只对 parented 子生效
      // （防御性 no-op 效果；tools 层已挡无 parent 目标）。改后由尾部 resolveLayout 重算。
      const t = objects.find(o => o.id === op.id);
      if (t) {
        if (op.position === 'absolute') t.position = 'absolute';
        else delete t.position;
      }
      break;
    }
    case 'reorder': {
      // SPEC-456: 移到所在栈底/顶 — 语义对齐 UI 手动拖排（store.ts REORDER_TO：index 0 = 最底）。
      // 有 parent：back = parent 子栈栈底（parent 对象之后最近位），front = 子栈栈顶（该 parent
      // 最后一个子对象之后）；无 parent（含 parent 已不存在的孤儿，防御性按根级）：back =
      // objects[0]，front = 数组尾。不存在的 id = no-op（与 update_text/move 的守卫一致）。
      const i = objects.findIndex(o => o.id === op.id);
      if (i >= 0) {
        const [moved] = objects.splice(i, 1);
        const pi = moved.parent !== undefined ? objects.findIndex(o => o.id === moved.parent) : -1;
        if (op.mode === 'back') {
          objects.splice(pi >= 0 ? pi + 1 : 0, 0, moved);
        } else if (pi >= 0) {
          let anchor = pi;
          for (let k = objects.length - 1; k > pi; k--) {
            if (objects[k].parent === moved.parent) { anchor = k; break; }
          }
          objects.splice(anchor + 1, 0, moved);
        } else {
          objects.push(moved);
        }
      }
      break;
    }
    case 'remove': {
      // SPEC-425: removing a frame cascades to all descendants (nested frames included).
      const doomed = new Set([op.id, ...collectDescendants(objects, op.id)]);
      objects = objects.filter(o => !doomed.has(o.id));
      break;
    }
    case 'set_image': {
      const t = objects.find(o => o.id === op.id);
      // Merged (web semantics): guarded src write + error reset — the unguarded worker copy
      // could clobber a live src with undefined and left stale error flags set.
      if (t) { if (op.src !== undefined) t.src = op.src; if (op.job_id) t.job_id = op.job_id; t.pending = op.pending ?? false; t.error = null; }
      break;
    }
    case 'set_bg':
      return { artboard: { ...canvas.artboard, bg: op.color }, objects: hasLayout(objects) ? resolveLayout(objects, options) : objects };
  }
  return { artboard: canvas.artboard, objects: hasLayout(objects) ? resolveLayout(objects, options) : objects };
}
